"use client";

import { Button } from "@nyntax/design-system/components/ui/button";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";

interface ShelfScrollerProps {
  children: ReactNode;
  nextLabel: string;
  previousLabel: string;
}

/**
 * A sideways row of cards with arrow buttons on wide screens (swipe on a
 * phone). The arrows hide at each end, so a row that fits shows none.
 *
 * @param props - The `<li>` cards and the arrow labels.
 * @returns The scrolling row.
 */
export const ShelfScroller = ({ children, nextLabel, previousLabel }: ShelfScrollerProps) => {
  const listRef = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });

  const measure = useCallback(() => {
    const list = listRef.current;

    if (list === null) {
      return;
    }

    setEdges({
      start: list.scrollLeft <= 1,
      end: list.scrollLeft + list.clientWidth >= list.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const page = (direction: 1 | -1) => {
    const list = listRef.current;

    list?.scrollBy({ left: direction * list.clientWidth * 0.9, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <ul
        className="-mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-4 pt-1 pb-3 [scrollbar-width:none] md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden"
        onScroll={measure}
        ref={listRef}
      >
        {children}
      </ul>
      {edges.start ? null : (
        <Button
          aria-label={previousLabel}
          className="absolute top-[28%] -left-4 hidden rounded-full shadow-lg md:inline-flex"
          onClick={() => {
            page(-1);
          }}
          size="icon-lg"
          variant="outline"
        >
          <LuChevronLeft aria-hidden="true" />
        </Button>
      )}
      {edges.end ? null : (
        <Button
          aria-label={nextLabel}
          className="absolute top-[28%] -right-4 hidden rounded-full shadow-lg md:inline-flex"
          onClick={() => {
            page(1);
          }}
          size="icon-lg"
          variant="outline"
        >
          <LuChevronRight aria-hidden="true" />
        </Button>
      )}
    </div>
  );
};
